import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { cloneDeep, isEqual } from 'lodash';

import { ensureRubricDocumentGuids } from '../RubricEditor/actions/rubric-editor-actions';
import {
  cloneDocumentWithGuids,
  findQuestionByGuids,
  findQuestionSetByGuid,
  getWeightOverridesMap,
  normalizeOverrideMapValue,
  overrideMapControlledInputValue,
  schemeNameTaken,
  setOverrideName,
  setOverrideWeightEntry,
  setQuestionWeight,
  setQuestionSetWeight,
  standardWeightDisplayNumber,
  standardWeightPlaceholderString,
  addWeightOverride,
  removeWeightOverride,
  normalizeWeightSectionForCompare,
} from './rubricWeightsUtils';

const RubricWeightsContext = createContext(null);

function standardWeightsEqual(a, b) {
  return standardWeightDisplayNumber(a) === standardWeightDisplayNumber(b);
}

function isDraftCommittedStandardDirtyForQuestionSet(
  draft,
  committed,
  questionSetGuid
) {
  const d = findQuestionSetByGuid(draft, questionSetGuid);
  const c = findQuestionSetByGuid(committed, questionSetGuid);
  if (!d || !c) return false;
  return !standardWeightsEqual(d.weight, c.weight);
}

function isDraftCommittedStandardDirtyForQuestion(
  draft,
  committed,
  questionSetGuid,
  questionGuid
) {
  const d = findQuestionByGuids(draft, questionSetGuid, questionGuid);
  const c = findQuestionByGuids(committed, questionSetGuid, questionGuid);
  if (!d || !c) return false;
  return !standardWeightsEqual(d.weight, c.weight);
}

function isDraftCommittedOverrideDirty(draft, committed, schemeId, nodeGuid) {
  const dMap = draft.weight_overrides?.[schemeId]?.weights;
  const cMap = committed.weight_overrides?.[schemeId]?.weights;
  const d = normalizeOverrideMapValue(dMap?.[nodeGuid]);
  const c = normalizeOverrideMapValue(cMap?.[nodeGuid]);
  if (d == null && c == null) return false;
  if (d == null || c == null) return true;
  return d !== c;
}

/**
 * Draft weights editing + lookups against last committed snapshot (last load/save).
 */
export function RubricWeightsProvider({ rubric, rubricSyncKey, children }) {
  const [draftDocument, setDraftDocument] = useState(() =>
    cloneDocumentWithGuids(ensureRubricDocumentGuids, rubric?.document)
  );
  const [committedDocument, setCommittedDocument] = useState(() =>
    cloneDocumentWithGuids(ensureRubricDocumentGuids, rubric?.document)
  );

  /** 'standard' | string (scheme map key) */
  const [selectedPanel, setSelectedPanel] = useState('standard');

  useEffect(() => {
    if (!rubric) return;
    const next = cloneDocumentWithGuids(
      ensureRubricDocumentGuids,
      rubric.document
    );
    setDraftDocument(next);
    setCommittedDocument(cloneDeep(next));
    setSelectedPanel((prev) => {
      if (prev === 'standard') {
        return 'standard';
      }
      const m = getWeightOverridesMap(next);
      return Object.prototype.hasOwnProperty.call(m, prev) ? prev : 'standard';
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rubricSyncKey tracks identity; `rubric` reference churns from redux denormalize
  }, [rubricSyncKey]);

  const overridesMap = getWeightOverridesMap(draftDocument);

  useEffect(() => {
    if (selectedPanel === 'standard') return;
    if (!Object.prototype.hasOwnProperty.call(overridesMap, selectedPanel)) {
      setSelectedPanel('standard');
    }
  }, [overridesMap, selectedPanel]);

  const isStandardView = selectedPanel === 'standard';
  const selectedSchemeId = isStandardView ? null : selectedPanel;

  const commitSavedSnapshot = useCallback((document) => {
    const next = cloneDocumentWithGuids(
      ensureRubricDocumentGuids,
      document ?? {}
    );
    setDraftDocument(next);
    setCommittedDocument(cloneDeep(next));
  }, []);

  const getCommittedStandardWeightByQuestionSetGuid = useCallback(
    (questionSetGuid) => {
      const qs = findQuestionSetByGuid(committedDocument, questionSetGuid);
      return standardWeightDisplayNumber(qs?.weight);
    },
    [committedDocument]
  );

  const getDraftStandardWeightByQuestionSetGuid = useCallback(
    (questionSetGuid) => {
      const qs = findQuestionSetByGuid(draftDocument, questionSetGuid);
      return standardWeightDisplayNumber(qs?.weight);
    },
    [draftDocument]
  );

  const getCommittedStandardWeightByQuestionGuid = useCallback(
    (questionSetGuid, questionGuid) => {
      const q = findQuestionByGuids(
        committedDocument,
        questionSetGuid,
        questionGuid
      );
      return standardWeightDisplayNumber(q?.weight);
    },
    [committedDocument]
  );

  const getDraftStandardWeightByQuestionGuid = useCallback(
    (questionSetGuid, questionGuid) => {
      const q = findQuestionByGuids(
        draftDocument,
        questionSetGuid,
        questionGuid
      );
      return standardWeightDisplayNumber(q?.weight);
    },
    [draftDocument]
  );

  const isWeightCellDirty = useCallback(
    (questionSetGuid, questionGuid) => {
      if (isStandardView) {
        if (questionGuid == null) {
          return isDraftCommittedStandardDirtyForQuestionSet(
            draftDocument,
            committedDocument,
            questionSetGuid
          );
        }
        return isDraftCommittedStandardDirtyForQuestion(
          draftDocument,
          committedDocument,
          questionSetGuid,
          questionGuid
        );
      }
      const nodeGuid = questionGuid ?? questionSetGuid;
      return isDraftCommittedOverrideDirty(
        draftDocument,
        committedDocument,
        selectedSchemeId,
        nodeGuid
      );
    },
    [committedDocument, draftDocument, isStandardView, selectedSchemeId]
  );

  const isOverridePresentInDraft = useCallback(
    (schemeId, nodeGuid) => {
      if (schemeId == null) return false;
      const weights = draftDocument.weight_overrides?.[schemeId]?.weights ?? {};
      return normalizeOverrideMapValue(weights[nodeGuid]) !== null;
    },
    [draftDocument.weight_overrides]
  );

  const onDraftQuestionSetWeightChange = useCallback(
    (questionSetGuid, weight) => {
      setDraftDocument((d) => setQuestionSetWeight(d, questionSetGuid, weight));
    },
    []
  );

  const onDraftQuestionWeightChange = useCallback(
    (questionSetGuid, questionGuid, weight) => {
      setDraftDocument((d) =>
        setQuestionWeight(d, questionSetGuid, questionGuid, weight)
      );
    },
    []
  );

  const onDraftOverrideWeightChange = useCallback(
    (nodeGuid, rawInput) => {
      if (selectedSchemeId == null) return;
      const parsed =
        rawInput === '' || String(rawInput).trim() === ''
          ? null
          : parseFloat(rawInput);
      setDraftDocument((d) =>
        setOverrideWeightEntry(d, selectedSchemeId, nodeGuid, parsed)
      );
    },
    [selectedSchemeId]
  );

  const onSetOverrideName = useCallback((schemeId, name) => {
    setDraftDocument((d) => setOverrideName(d, schemeId, name));
  }, []);

  const onAddWeightOverride = useCallback((schemeName, schemeId) => {
    setDraftDocument((d) => addWeightOverride(d, schemeName, schemeId));
  }, []);

  const onRemoveWeightOverride = useCallback((schemeId) => {
    setDraftDocument((d) => removeWeightOverride(d, schemeId));
  }, []);

  const isSchemeNameTaken = useCallback(
    (name, exceptSchemeId = null) =>
      schemeNameTaken(draftDocument.weight_overrides, name, exceptSchemeId),
    [draftDocument.weight_overrides]
  );

  const selectedOverrideEntry = useMemo(() => {
    if (selectedSchemeId == null) return null;
    const raw = draftDocument.weight_overrides?.[selectedSchemeId];
    if (!raw || typeof raw !== 'object') return null;
    return { id: selectedSchemeId, ...raw };
  }, [draftDocument.weight_overrides, selectedSchemeId]);

  const draftFingerprint = useMemo(
    () => normalizeWeightSectionForCompare(draftDocument),
    [draftDocument]
  );
  const committedFingerprint = useMemo(
    () => normalizeWeightSectionForCompare(committedDocument),
    [committedDocument]
  );
  const hasDraftChanges = useMemo(
    () => !isEqual(draftFingerprint, committedFingerprint),
    [draftFingerprint, committedFingerprint]
  );

  const value = useMemo(
    () => ({
      draftDocument,
      committedDocument,
      hasDraftChanges,
      commitSavedSnapshot,
      selectedPanel,
      setSelectedPanel,
      isStandardView,
      selectedSchemeId,
      selectedOverrideEntry,
      getDraftStandardWeightByQuestionSetGuid,
      getCommittedStandardWeightByQuestionSetGuid,
      getDraftStandardWeightByQuestionGuid,
      getCommittedStandardWeightByQuestionGuid,
      standardWeightPlaceholderString,
      overrideMapControlledInputValue,
      isWeightCellDirty,
      isOverridePresentInDraft,
      onDraftQuestionSetWeightChange,
      onDraftQuestionWeightChange,
      onDraftOverrideWeightChange,
      onSetOverrideName,
      onAddWeightOverride,
      onRemoveWeightOverride,
      isSchemeNameTaken,
    }),
    [
      draftDocument,
      committedDocument,
      hasDraftChanges,
      commitSavedSnapshot,
      selectedPanel,
      isStandardView,
      selectedSchemeId,
      selectedOverrideEntry,
      getDraftStandardWeightByQuestionSetGuid,
      getCommittedStandardWeightByQuestionSetGuid,
      getDraftStandardWeightByQuestionGuid,
      getCommittedStandardWeightByQuestionGuid,
      isWeightCellDirty,
      isOverridePresentInDraft,
      onDraftQuestionSetWeightChange,
      onDraftQuestionWeightChange,
      onDraftOverrideWeightChange,
      onSetOverrideName,
      onAddWeightOverride,
      onRemoveWeightOverride,
      isSchemeNameTaken,
    ]
  );

  return (
    <RubricWeightsContext.Provider value={value}>
      {children}
    </RubricWeightsContext.Provider>
  );
}

export function useRubricWeights() {
  const ctx = useContext(RubricWeightsContext);
  if (!ctx) {
    throw new Error(
      'useRubricWeights must be used within RubricWeightsProvider'
    );
  }
  return ctx;
}

export { RubricWeightsContext };
